// /c/{id}/a/{key}: one assignment, every student on it — their shares to
// it, their recordings of it, the interventions in them — and who has not
// started (RFC-043 model 19, byj-1wv.4.12), as /shares lists: students who
// started under their heading, then those who have not; under a student who
// started, their sessions as the dashboard's rows and their recordings as
// rows of their own, and the files they handed in (RFC-054 §5) as theirs.
import { useEffect, useState } from "react";
import { api, type AssignmentFile, type AssignmentPage as PageData, type AssignmentRow } from "../api";
import { asShown, type SeenMark } from "../classroom";
import { RecordingLine } from "../components/ClassRecordings";
import { ClassSessionRow } from "../components/ClassSessionRow";
import { useSeenAlerts } from "../components/SessionWatch";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLoading, PageNotice } from "../components/Page";
import { Grouped } from "../components/Rows";
import { ScoreChip } from "../components/ScoreChip";
import { useNow } from "../now";
import { t, when, whenMinute, whenShort } from "../i18n";
import { reviewWords } from "../files";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** A file a student handed in, opened on its page; late when its latest version was. */
function FileLine({ f }: { f: AssignmentFile }) {
  return (
    <li className="share-row rec-row is-ended no-menu">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {f.kind === "folder" ? "dir" : "fil"}
      </span>
      <div className="row-main">
        <Link to={`/f/${encodeURIComponent(f.id)}`} className="row-title">
          {f.name}
        </Link>
        <div className="row-sub">{t("files:fileVersion", { n: f.latest })}</div>
      </div>
      <div className="row-meta">
        {f.late && <span className="chip chip-warn">{t("files:fileLate")}</span>}
        {f.review && (
          // checked or returned, its mark: the instructor's own check, counted on the page (byj-1wv.136)
          <span className={`chip ${f.review.state === "checked" ? "chip-ok" : "chip-warn"}`} data-testid="hand-in-review">
            {reviewWords(f.review, f.latest)}
          </span>
        )}
        <time className="row-time" dateTime={f.updated_at} title={when(f.updated_at)}>
          {whenShort(f.updated_at)}
        </time>
      </div>
    </li>
  );
}

function StudentBlock({ r, now, seen }: { r: AssignmentRow; now: number; seen: Record<string, SeenMark> }) {
  const files = r.files ?? [];
  return (
    <li className="student-block">
      <div className="student-line row wrap">
        <strong className="grow">{r.student.name}</strong>
        {r.started && (
          <span className="muted small">
            {t("classes:sharesN", { count: r.shares.length })} · {t("classes:recordingsN", { count: r.recordings.length })}
            {files.length > 0 && <> · {t("files:filesN", { count: files.length })}</>} · {t("classes:notesN", { count: r.notes })} · {t("classes:injectionsN", { count: r.injections })}
          </span>
        )}
        {r.score && <ScoreChip score={r.score} />}
        <span className={`chip ${r.started ? "chip-live" : ""}`} data-testid="started">
          {r.started ? t("classes:started") : t("classes:notStarted")}
        </span>
      </div>
      {(r.shares.length > 0 || r.recordings.length > 0 || files.length > 0) && (
        <ul className="rows rows-nested">
          {r.shares.map((s) => (
            // in the state and with the alerts the dashboard shows it in
            <ClassSessionRow key={s.share} s={asShown(s, seen[s.share])} now={now} titled="session" />
          ))}
          {r.recordings.map((rec) => (
            <RecordingLine key={rec.id} rec={rec} />
          ))}
          {files.map((f) => (
            <FileLine key={f.id} f={f} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function AssignmentPage({ classId, assignmentKey, load = api.assignmentPage }: { classId: string; assignmentKey: string; load?: (id: string, key: string) => Promise<PageData> }) {
  const [page, setPage] = useState<PageData | null>(null);
  const [error, setError] = useState<string | null>(null);
  // how long ago, said again while the page is open (now.ts)
  const now = useNow();
  const [seen] = useSeenAlerts(classId);
  useEffect(() => {
    load(classId, assignmentKey).then(setPage, (err: unknown) => setError(errText(err)));
  }, [classId, assignmentKey, load]);
  if (error) {
    return (
      <PageNotice error={error}>
        <Link to={`/c/${encodeURIComponent(classId)}`}>{t("classes:dashboard")}</Link>
      </PageNotice>
    );
  }
  if (!page) return <PageLoading />;
  const a = page.assignment;
  const started = page.students.filter((r) => r.started);
  return (
    <Page>
      <PageHead
        title={a.title}
        meta={
          <>
            <code>{a.key}</code>
            <span className="muted">{a.due_at ? t("classes:dueOn", { at: whenMinute(a.due_at) }) : t("classes:noDue")}</span>
            {a.archived_at && <span className="chip">{t("classes:archived")}</span>}
            <span className="chip chip-live" data-testid="started-count">
              {t("classes:started")} {started.length}/{page.students.length}
            </span>
          </>
        }
        actions={
          <Link to={`/c/${encodeURIComponent(classId)}`} className="button">
            {t("classes:dashboard")}
          </Link>
        }
      />
      {a.text && <p className="pre-wrap assignment-text">{a.text}</p>}
      {page.students.length === 0 ? (
        <p className="muted">{t("classes:noStudents")}</p>
      ) : (
        <div className="rows-panel" data-testid="assignment-students">
          <Grouped
            id="students"
            groups={[
              { title: t("classes:startedGroup"), items: started, className: "rows-head-live" },
              { title: t("classes:notStartedGroup"), items: page.students.filter((r) => !r.started) },
            ]}
            row={(r) => <StudentBlock key={r.student.id} r={r} now={now} seen={seen} />}
          />
        </div>
      )}
    </Page>
  );
}
